import z from 'zod';

export type CreateReportFormData = z.infer<typeof createReportSchema>;
export type CreateReportPropertyFormData = z.infer<typeof createReportPropertySchema>;
export type CreateReportPurchaseFormData = z.infer<typeof createReportPurchaseSchema>;
export type CreateReportGrowthRatesFormData = z.infer<typeof createReportGrowthRatesSchema>;
export type CreateReportExpenseFormData = z.infer<typeof createReportExpenseSchema>;
export type CreateReportRevenueFormData = z.infer<typeof createReportRevenueSchema>;

export const createReportPropertySchema = z.object({
  street: z.string().regex(/\d+(\s+\S+)+/, 'Must include a street number and street name'),
  unit: z.string().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  zipCode: z
    .string()
    .regex(/\d{5}(-\d{4})?/, 'Must be a 5-digit ZIP code or a 9-digit hyphenated zip code (ZIP+4).')
});

export const createReportPurchaseSchema = z.object({
  purchasePrice: z.number().positive().int(),
  salesCommission: z.number().min(0).max(100),
  downPayment: z.number().min(0).int(),
  interestRate: z.number().min(0).max(100),
  term: z.number().min(8).max(30),
  closingCosts: z.number().min(0).int(),
  isRehab: z.boolean(),
  rehabCosts: z.number().min(0).default(0),
  arv: z.number().min(0).default(0),
  taxes: z.number().min(0).int(),
  insurance: z.number().min(0).int(),
  pmi: z.number().min(0).default(0),
  furnishings: z.number().default(0)
});

export const createReportGrowthRatesSchema = z.object({
  appreciation: z.number().min(0).max(100).default(0),
  revenueGrowthRate: z.number().min(0).max(100).default(0),
  expenseGrowthRate: z.number().min(0).max(100).default(0)
});

export const createReportExpenseSchema = z.object({
  electricity: z.number().min(0).default(0),
  internet: z.number().min(0).default(0),
  utilities: z.number().min(0).default(0),
  gas: z.number().min(0).default(0),
  hoaFees: z.number().min(0).default(0),
  maintenance: z.number().min(0).default(0),
  capitalExpenditures: z.number().min(0).default(0),
  managementFees: z.number().min(0).default(0),
  bookingPlatformFees: z.number().min(0).default(0),
  cleaningFees: z.number().min(0).default(0),
  cleansPerMonth: z.number().min(0).default(0),
  landscaping: z.number().min(0).default(0),
  snowRemoval: z.number().min(0).default(0),
  pestControl: z.number().min(0).default(0),
  legalFees: z.number().min(0).default(0),
  permitAndLicenseFees: z.number().min(0).default(0)
});

export const createReportRevenueSchema = z.object({
  nightlyRevenue: z.number().min(0).int().default(0),
  occupancy: z.number().min(0).max(100).int().default(0)
});

export const createReportSchema = createReportPropertySchema
  .merge(createReportPurchaseSchema)
  .merge(createReportGrowthRatesSchema)
  .merge(createReportExpenseSchema)
  .merge(createReportRevenueSchema);
